// ==========================
// Version 3 — pages/NFTTestPage.jsx
// - Uses thirdweb v5 + thirdwebClient
// ==========================
import React from "react";
import { getContract } from "thirdweb";
import { useReadContract } from "thirdweb/react";
import { getNFTs } from "thirdweb/extensions/erc721";
import { polygonAmoy } from "thirdweb/chains";

import { thirdwebClient } from "../thirdwebClient";
import { NFT_COLLECTION_ADDRESS } from "../thirdweb.config";
import Seo from "../components/Seo";

const NAVY = "#0A1A33";
const IVORY = "#FDFCF9";
const GOLD = "#C9A44C";
const WARM_GRAY = "#888888";
const BURGUNDY = "#5C1A2B";

// Create a typed contract instance once (module-level)
const contract = getContract({
  client: thirdwebClient,
  address: NFT_COLLECTION_ADDRESS,
  chain: polygonAmoy,
});

function NFTTestPage() {
  const {
    data: nfts,
    isLoading,
    error,
  } = useReadContract(getNFTs, {
    contract,
    start: 0,
    count: 60,
  });

  return (
    <div style={pageStyles.page}>
      <Seo
        title="NFT Test — Blessed With A Kiss"
        description="Internal page to verify connection to the BWAK NFT collection on Polygon Amoy."
        type="website"
      />

      <div style={pageStyles.card}>
        <h1 style={pageStyles.title}>NFT Collection Test</h1>
        <p style={pageStyles.sub}>
          Contract address:
          <code style={pageStyles.code}>{NFT_COLLECTION_ADDRESS}</code>
        </p>

        {isLoading && (
          <p style={pageStyles.status}>Loading contract &amp; NFTs…</p>
        )}

        {error && (
          <div style={pageStyles.errorBox}>
            <strong>Something went wrong.</strong>
            <p style={{ margin: "4px 0 0", fontSize: 13 }}>
              {error.message || String(error)}
            </p>
          </div>
        )}

        {!isLoading && !error && (
          <>
            {!nfts || nfts.length === 0 ? (
              <p style={pageStyles.status}>
                No NFTs found yet. Mint one from the app and refresh this page.
              </p>
            ) : (
              <div style={pageStyles.grid}>
                {nfts.map((nft) => {
                  const meta = nft.metadata || {};
                  const id =
                    typeof nft.id === "bigint"
                      ? nft.id.toString()
                      : meta.id ?? "";

                  return (
                    <article
                      key={`${nft.tokenAddress}-${id}`}
                      style={pageStyles.nftCard}
                    >
                      {meta.image && (
                        <img
                          src={meta.image}
                          alt={meta.name || "NFT image"}
                          style={pageStyles.img}
                          loading="lazy"
                          decoding="async"
                        />
                      )}
                      <h2 style={pageStyles.nftTitle}>
                        {meta.name || `Token #${id}`}
                      </h2>
                      {meta.description && (
                        <p style={pageStyles.nftDesc}>{meta.description}</p>
                      )}
                      <p style={pageStyles.tokenId}>Token ID: {id}</p>
                    </article>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const pageStyles = {
  page: {
    minHeight: "calc(100dvh - 64px)",
    background: NAVY,
    padding: "24px 12px 40px",
  },
  card: {
    maxWidth: 960,
    margin: "0 auto",
    background: IVORY,
    color: NAVY,
    borderRadius: 18,
    border: "1px solid rgba(201,164,76,0.4)",
    boxShadow: "0 16px 40px rgba(10,26,51,0.35)",
    padding: 20,
  },
  title: {
    margin: "0 0 4px",
    fontFamily: "serif",
    fontSize: 26,
  },
  sub: {
    margin: "0 0 14px",
    fontSize: 13,
    color: WARM_GRAY,
  },
  code: {
    marginLeft: 6,
    padding: "2px 6px",
    borderRadius: 6,
    background: "rgba(10,26,51,0.06)",
    border: "1px solid rgba(10,26,51,0.12)",
    fontSize: 12,
    fontFamily: "monospace",
  },
  status: {
    marginTop: 8,
    fontSize: 14,
    color: WARM_GRAY,
  },
  errorBox: {
    marginTop: 8,
    padding: 10,
    borderRadius: 10,
    border: "1px solid rgba(183,110,121,0.55)",
    background: "rgba(92,26,43,0.08)",
    color: BURGUNDY,
    fontSize: 14,
  },
  grid: {
    marginTop: 16,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
  },
  nftCard: {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid rgba(201,164,76,0.35)",
    padding: 10,
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  img: {
    width: "100%",
    borderRadius: 10,
    objectFit: "cover",
    maxHeight: 260,
  },
  nftTitle: {
    margin: "4px 0 0",
    fontSize: 15,
    fontWeight: 700,
  },
  nftDesc: {
    margin: 0,
    fontSize: 13,
    color: WARM_GRAY,
  },
  tokenId: {
    margin: "4px 0 0",
    fontSize: 12,
    color: WARM_GRAY,
  },
};

export default NFTTestPage;
// ==========================
// End Version 3 — pages/NFTTestPage.jsx
// ==========================
